import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { _, core, IssueLevel } from '@icc/shared/helpers';
import { ConfigurationsService, CurrentConfiguratorService, ICC_PAGE_DATA, ModalService, PageComponent } from '@icc/configurator/shared';
import { SharedFacade } from '@icc/configurator/shared';
import { Casing, Profile, ProfileType, Reinforcement, WindowActiveConfiguration } from '@icc/window-door/types';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { TranslateService, APP_CONFIG, AppConfigFactory, IssuesService } from '@icc/shared';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NewColorsService, ProfileColorsService, ProfilesService } from '@icc/construction-elements/configurator';
import { ProfilesPriceService } from '@icc/construction-elements/configurator';
import { PriceBaseService } from '@icc/price/b2b';
import { PriceColorsService } from '@icc/construction-elements/configurator';
import { UnitConverterService } from '@icc/configurator/ui';
import { ReinforcementsModalService } from '../../reinforcements/reinforcements-modal.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { gapValidator } from './gap-validator';
import { MatCheckbox } from '@angular/material/checkbox';
import { CasingsService } from '../casings.service';
import { DoorPortalsService, PortalOptions } from '../../door-portals/door-portals.service';
import { ProfileInfoComponent } from '@icc/construction-elements/configurator';
import { ConfiguratorsDataService } from '@icc/shared/configurators/configurators-data.service';
import { ReinforcementsService } from '../../reinforcements/reinforcements.service';
import { IccProfileSideColors } from '@icc/construction-elements/types';

@Component({
    selector: 'icc-profile-options-page',
    templateUrl: './profile-options-page.component.html',
    styleUrls: ['./profile-options-page.component.scss'],
    standalone: false
})
export class ProfileOptionsPageComponent extends PageComponent implements OnInit, OnDestroy {
    public title: string = _('ACCESSORY|Opcje profilu');

    profile: Profile | null = null;
    form: UntypedFormGroup;

    private formSubscriptions = new Subscription();
    casingForm!: UntypedFormGroup;
    casingWallOverlap: number[] = [];
    casings: any = [];
    casing: Casing | null = null;
    isCasingOptionsSelected = false;
    isCasingAvailableForFrame = false;
    selectedCasing: any;
    CASING_GAP = 10;
    lowestInnerCasingWidth = 0;
    highestInnerCasingWidth = 0;
    innerCasingWidth = 0;
    casingWallWidth!: number;
    casingHitchPoint = 0;

    focused = false;
    showColorsWhenCoupling = this.currentConfiguratorService.conf === 'coupled_window';
    showColorsInComplementary = this.currentConfiguratorService.conf === 'complementary_goods';
    get canChangeProfileColor() {
        return !(this.profile?.type === 'coupling' && this.profile.widthOutType);
    }
    showCoreColorInAdditionalProfilesAndAccessories = this.config().IccConfig.Configurators
        .showCoreColorInAdditionalProfilesAndAccessories;
    currency: any = null;
    optionsInComplementary = this.currentConfiguratorService.conf === 'complementary_goods';

    dimensionUnit = this.unitConverterService.getUnit();

    portalForm!: UntypedFormGroup;
    availablePortals: any[] = [];
    isPortalSelected = false;
    selectedPortal: any = null;
    portalPlateTypes: string[] = [];
    leftSideWidths: number[] = [];
    rightSideWidths: number[] = [];
    upperHeights: number[] = [];
    isDoorPortalFormOpen = false;
    portalMinimumWallThickness = 0;
    portalMaximumWallThickness = 0;
    selectedPortalMinimumWallThickness = 0;
    selectedPortalMaximumWallThickness = 0;
    portalGap = 0;
    additionalWallThicknessFromFrame = 0;
    selectedPortalOptions: PortalOptions = {};

    isWysiwygEditorContentEmpty = true;
    hideExtensionColorSelection = false;
    noFreeExtensionColorSelection = false;

    hiddenPrice = false;

    whiteColorButtonVisible: boolean | null = false;
    windowColorButtonVisible: boolean | null = false;
    anyColorButtonVisible: boolean | null = false;
    isWindowColor = false;

    get listPriceEnable() {
        return this.config().IccConfig.Offer.listPrice;
    }

    get listPriceEnableInMarket() {
        const conf = this.configurationsService.conf?.Current;
        const isFullDoorUnchecked = conf && 'Components' in conf && (conf as any).Components?.fullDoor === false;
        return isFullDoorUnchecked ? false : this.config().listPriceEnableInMarket;
    }

    invalidLength = false;

    constructor(
        private configuratorsDataService: ConfiguratorsDataService,
        private translateService: TranslateService,
        private configurationsService: ConfigurationsService<'window'>,
        private currentConfiguratorService: CurrentConfiguratorService,
        private priceBaseService: PriceBaseService,
        private sharedFacade: SharedFacade,
        private profileColorsService: ProfileColorsService,
        private newColorsService: NewColorsService,
        private profilesPriceService: ProfilesPriceService,
        private fb: UntypedFormBuilder,
        @Inject(APP_CONFIG) public config: AppConfigFactory,
        @Inject(ICC_PAGE_DATA)
        private pageData: {
            profile: Profile;
            priceBaseType?: string;
            profilesPrices: any[];
            profile$: BehaviorSubject<Profile>,
            selectedColors$: BehaviorSubject<any>,
            colorGroups: any[],
        },
        private unitConverterService: UnitConverterService,
        private reinforcementsModalService: ReinforcementsModalService,
        private casingsService: CasingsService,
        private profilesSerivce: ProfilesService,
        private doorPortalsService: DoorPortalsService,
        private modalService: ModalService,
        private priceColorsService: PriceColorsService,
        private issuesService: IssuesService,
        private reinforcementsService: ReinforcementsService,
    ) {
        super();
    }

    ngOnInit() {
        this.hiddenPrice =
            (this.config().preset === 'b2c' && this.config().hidePricesInB2cConfigurator)
            || (this.config().IccConfig.Configurators.hideProfilePrice);
        this.isWysiwygEditorContentEmpty = Boolean(
            !this.pageData?.profile?.wysiwygEditorContent?.length
        );
        this.selectedPortalOptions = this.doorPortalsService?.selectedPortalOptions;
        this.isDoorPortalFormOpen = Boolean(this.selectedPortalOptions?.isPortalSelected);

        if (this.currentConfiguratorService.conf !== 'complementary_goods') {
            this.availablePortals = this.profilesSerivce.matchProfileToFrame(
                this.pageData.profile?.id,
                'portal'
            );
            if (this.doorPortalsService.getOpeningType() === 'inward') {
                this.availablePortals = this.availablePortals.filter((p) => p?.inwardOpening);
            } else {
                this.availablePortals = this.availablePortals.filter((p) => p?.outwardOpening);
            }

            this.casings = this.profilesSerivce.matchProfileToFrame(
                this.pageData.profile?.id,
                'casing'
            );

            this.casings.length > 0 && this.casingSetup();
            this.availablePortals.length > 0 && this.portalSetup();
        }

        this.profile = this.pageData.profile;
        this.ensureProfilePricingColors();

        this.form = this.fb.group({
            count: 1,
            currentLength: [0, [Validators.min(0), Validators.required]],
            length: [this.profile.length || 0, [Validators.min(this.profile.minLength), Validators.max(this.profile.maxLength), Validators.required]],
            width: [this.profile.width || 0],
            comment: '',
        });

        this.updateColorsSelect();
        this.pageData.selectedColors$.subscribe((c) => {
            if (this.profile) {
                this.profile.selectedColor = c;
                this.updateColorsSelect();
            }
        });
        if (this.profile.widthOutType && !this.profile.selectedWidth) {
            this.profile.selectedWidth = this.profile.widthOut || this.profile.widthOutMin;
        }
        if (this.profile.widthOutType) {
            this.updateProfilePrice(this.profile);
        }


        this.sharedFacade.currency$.subscribe((currency) => {
            this.currency = currency;
        });

        this.pageData.profile$.subscribe((p) => {
            this.form.patchValue(p);
        });
        if (this.profile.notStandardLength && this.profile.isDefaultLength === false) {
            this.validateLength();
        }
    }
    updateProfilePrice(profile: Profile) {
        if (!profile.widthOutType || !profile.selectedWidth) {
            return;
        }

        const profileColors = this.resolveProfilePricingColors(profile);

        const widthOutRanges = this.getNormalizedWidthOutRanges(profile.widthOutRanges);
        const originalType = this.pageData.priceBaseType || (profile as any).originalType || profile.type;
        let dynamicType: string = originalType;
        if (widthOutRanges.length) {
            const rangeIndex = widthOutRanges.findIndex((range: any) => {
                const from = Number(range?.from);
                const to = Number(range?.to);
                return !Number.isNaN(from) && !Number.isNaN(to)
                    && Number(profile.selectedWidth) >= from
                    && Number(profile.selectedWidth) <= to;
            });
            if (rangeIndex !== -1) {
                dynamicType = `${dynamicType}_range_${rangeIndex}`;
            }
        }

        const system = this.configurationsService.conf.Current.System;
        if (originalType === 'coupling') {
            const couplingPriceSystem = this.currentConfiguratorService.conf === 'coupled_window'
                ? null
                : system;
            profile.price = this.profilesPriceService.getProfilePrice(
                profile.id,
                'coupling',
                couplingPriceSystem,
                profileColors,
                this.pageData.profilesPrices,
                undefined,
                undefined,
                undefined,
                undefined,
                profile.selectedWidth,
                {
                    ...profile,
                    selectedWidth: profile.selectedWidth,
                    widthOut: profile.selectedWidth,
                }
            );
        } else {
            const priceSystem = system && system.id ? system : null;
            const colorFactor = this.getProfileColorFactor(profile, profileColors);
            profile.price = this.profilesPriceService.getProfilePrice(
                profile.id,
                dynamicType,
                priceSystem,
                profileColors,
                this.pageData.profilesPrices,
                undefined,
                undefined,
                undefined,
                colorFactor,
                profile.selectedWidth,
                {
                    ...profile,
                    originalProfile: (profile as any).originalProfile,
                    originalType,
                }
            );
        }
        profile.price = this.normalizePartialPrice(profile.price);
    }

    private getProfileColorFactor(profile: Profile, profileColors: IccProfileSideColors) {
        const isComplementaryGoods =
            this.currentConfiguratorService.conf === 'complementary_goods';
        const currentSystem = this.configurationsService.conf.Current.System;
        const profileSystemIds = profile.systems?.map(Number) ?? [];
        const useCurrentSystem =
            !isComplementaryGoods ||
            Boolean(currentSystem?.id && profileSystemIds.includes(Number(currentSystem.id)));
        const system = useCurrentSystem ? currentSystem : null;
        const profileColorGroupIds = new Set([
            ...(profile.priceLevelColorGroups ?? []),
            ...(profile.priceLevelColorGroupsOut ?? []),
        ].map(Number));
        const colorGroups = isComplementaryGoods
            ? (this.pageData.colorGroups || []).filter((group) =>
                profileColorGroupIds.has(Number(group.id))
            )
            : this.pageData.colorGroups || [];

        return this.priceColorsService.getSideProfileColorFactor({
            profile,
            profileColors,
            wood: profile.selectedWood,
            system,
            colorGroups,
        });
    }

    private ensureProfilePricingColors() {
        if (!this.profile) {
            return;
        }

        const conf = this.configurationsService.conf.Current;
        if (!WindowActiveConfiguration.is(conf)) {
            return;
        }

        const hasSelectedColor = !!(
            this.profile.selectedColor?.outer
            || this.profile.selectedColor?.inner
            || this.profile.selectedColor?.core
        );

        if (hasSelectedColor) {
            return;
        }

        const fallbackColors =
            this.pageData.selectedColors$?.value
            || conf.Colors?.frame
            || {
                outer: null,
                inner: null,
                core: null,
                alushell: null,
            };

        this.profile.selectedColor = this.profileColorsService.setDefaultProfileColorSet(
            conf,
            this.profile,
            fallbackColors
        );
        this.pageData.selectedColors$?.next(this.profile.selectedColor);
    }

    private resolveProfilePricingColors(profile: Profile) {
        const hasSelectedColor = !!(
            profile.selectedColor?.outer
            || profile.selectedColor?.inner
            || profile.selectedColor?.core
        );

        if (hasSelectedColor) {
            return profile.selectedColor;
        }

        this.ensureProfilePricingColors();
        return profile.selectedColor;
    }

    private getNormalizedWidthOutRanges(widthOutRanges: any): any[] {
        if (Array.isArray(widthOutRanges)) {
            return widthOutRanges;
        }

        if (typeof widthOutRanges === 'string') {
            const parsed = core.parseJson(widthOutRanges);
            return Array.isArray(parsed) ? parsed : [];
        }

        return [];
    }

    private normalizePartialPrice(price: any) {
        if (!price || typeof price !== 'object') {
            return price;
        }

        const priceFields = [
            'price_construction',
            'price_piece',
            'price_length',
            'price_field',
            'price_percent',
            'price_sash',
            'price_two_sashes',
            'price_area',
            'list_price_construction',
            'list_price_piece',
            'list_price_length',
            'list_price_field',
            'list_price_percent',
            'list_price_sash',
            'list_price_two_sashes',
            'list_price_area',
            'settlement_length',
        ];

        const hasAnyValue = priceFields.some((field) => price[field] !== null && price[field] !== undefined);
        if (!hasAnyValue) {
            return price;
        }

        priceFields.forEach((field) => {
            if (price[field] === null) {
                price[field] = 0;
            }
        });

        return price;
    }

    onSelectedWidthChange(width: number | string) {
        if (!this.profile || !this.profile.widthOutType) {
            return;
        }

        const numericWidth = Number(width);
        if (!Number.isFinite(numericWidth) || numericWidth <= 0) {
            return;
        }

        this.profile.selectedWidth = numericWidth;
        this.updateProfilePrice(this.profile);
    }

    onAngleChange(angle: number | string) {
        if (!this.profile || this.profile.couplingType !== 'angle_coupling') {
            return;
        }

        const numericAngle = Number(angle);
        if (!Number.isFinite(numericAngle)) {
            return;
        }

        this.profile.angle = numericAngle;
    }


    private casingSetup() {
        if (this.pageData.profile.onlyWithCasingCover) {
            this.isCasingOptionsSelected = true;
            this.casingsService.setIsCasingAvailableForFrame(true);
        } else {
            this.isCasingOptionsSelected = false;
            this.casingsService.setIsCasingAvailableForFrame(false);
        }

        this.setInitialCasingSetup(this.casings);
        this.createCasingFormGroup(this.casings, this.casingHitchPoint);

        this.formSubscriptions.add(
            this.casingForm
                .get('casingWallWidth')
                ?.valueChanges.pipe(distinctUntilChanged())
                .subscribe((wallWidth) => {
                    const casing = this.casingsService.getCasingBasedOnWallWidth(
                        Number(wallWidth),
                        this.casingHitchPoint,
                        this.casings
                    );
                    this.innerCasingWidth = wallWidth - this.casingHitchPoint;
                    this.casingWallOverlap = this.casingsService.getWallOverlapsBasedOnInnerCasingWidth(
                        casing,
                        this.innerCasingWidth
                    );
                    this.casingForm
                        .get('casingWallOverlap')
                        ?.patchValue(this.casingWallOverlap[0], { emitEvent: false });
                    this.changeCasingBasedOnInnerWidthAndWallOverlap();
                })
        );
    }

    private portalSetup() {
        if (this.configurationsService.conf.Current.doorPortal?.id) {
            this.isPortalSelected = true;
        } else {
            this.isPortalSelected = false;
        }

        this.setInitialPortalSetup();
        this.createPortalFormGroup();
        this.formSubscriptions.add(
            this.portalForm
                .get('wallThickness')
                ?.valueChanges.pipe(distinctUntilChanged())
                .subscribe((wallThickness) => {
                    const portal = this.doorPortalsService.getPortalBasedOnWallThickness(
                        Number(wallThickness),
                        this.additionalWallThicknessFromFrame,
                        this.availablePortals
                    );

                    if (!portal) {
                        return;
                    }

                    const {
                        leftSideWidths,
                        rightSideWidths,
                        upperHeights,
                    } = this.doorPortalsService.getPortalSizesForPortalSides(portal);
                    this.leftSideWidths = leftSideWidths;
                    this.rightSideWidths = rightSideWidths;
                    this.upperHeights = upperHeights;

                    if (
                        !this.leftSideWidths.includes(
                            Number(this.portalForm.get('leftWidth')?.value)
                        )
                    ) {
                        this.portalForm
                            .get('leftWidth')
                            ?.patchValue(this.leftSideWidths[0], { emitEvent: false });
                    }

                    if (
                        !this.rightSideWidths.includes(
                            Number(this.portalForm.get('rightWidth')?.value)
                        )
                    ) {
                        this.portalForm
                            .get('rightWidth')
                            ?.patchValue(this.rightSideWidths[0], { emitEvent: false });
                    }

                    if (
                        !this.upperHeights.includes(
                            Number(this.portalForm.get('upperHeight')?.value)
                        )
                    ) {
                        this.portalForm
                            .get('upperHeight')
                            ?.patchValue(this.upperHeights[0], { emitEvent: false });
                    }

                    this.selectedPortal = portal;
                    this.portalGap = portal.portalGap;
                })
        );

        this.portalForm.valueChanges.subscribe((val) => {
            val.isPortalSelected = true;
            this.doorPortalsService.setSelectedPortalOptions(val);
        });
    }
    togglePortal(event: MatCheckbox) {
        if (event.checked) {
            this.isDoorPortalFormOpen = true;
            this.isPortalSelected = true;
            this.doorPortalsService.setSelectedPortalOptions({ isPortalSelected: true });
        } else {
            this.isDoorPortalFormOpen = false;
            this.isPortalSelected = false;
            this.doorPortalsService.setSelectedPortalOptions({ isPortalSelected: false });
        }
    }

    setInitialPortalSetup(conf = this.configurationsService.conf.Current) {
        this.additionalWallThicknessFromFrame = this.pageData.profile?.additionalWallThickness;
        this.portalMinimumWallThickness =
            Math.min(...this.availablePortals.map((c) => c.minimumPortalDepth)) +
            this.additionalWallThicknessFromFrame;
        this.portalMaximumWallThickness =
            Math.max(...this.availablePortals.map((c) => c.maximumPortalDepth)) +
            this.additionalWallThicknessFromFrame;
        const portal = this.doorPortalsService.getAvailableSelectedPortal(
            conf,
            this.availablePortals
        );
        this.portalGap = portal.portalGap ? portal.portalGap : 0;

        if (this.availablePortals.find((p) => p.id === conf.doorPortal?.id)) {
            this.selectedPortal = conf.doorPortal || portal;
        } else {
            this.selectedPortal = portal;
        }

        if (conf.doorPortal?.id && portal) {
            const {
                leftSideWidths,
                rightSideWidths,
                upperHeights,
            } = this.doorPortalsService.getPortalSizesForPortalSides(portal);
            this.setPortalSizesForPortalSides(leftSideWidths, rightSideWidths, upperHeights);
            this.portalPlateTypes = this.doorPortalsService.getPortalPlateType(portal);
        } else {
            const {
                leftSideWidths,
                rightSideWidths,
                upperHeights,
            } = this.doorPortalsService.getPortalSizesForPortalSides(this.availablePortals[0]);
            this.setPortalSizesForPortalSides(leftSideWidths, rightSideWidths, upperHeights);
            this.portalPlateTypes = this.doorPortalsService.getPortalPlateType(
                this.availablePortals[0]
            );
        }
    }

    createPortalFormGroup(conf = this.configurationsService.conf.Current) {
        const portal = this.availablePortals.find((p) => p.id === conf.doorPortal?.id);

        this.portalForm = this.fb.group({
            plateType: [
                portal
                    ? conf.doorPortal?.plateType
                    : this.selectedPortalOptions?.plateType
                    ? this.selectedPortalOptions.plateType
                    : this.portalPlateTypes[0],
            ],
            wallThickness: [
                portal
                    ? conf.doorPortal?.wallThickness
                    : this.selectedPortalOptions?.wallThickness
                    ? this.selectedPortalOptions.wallThickness
                    : this.portalMinimumWallThickness,
                {
                    validators: [
                        Validators.min(Number(this.portalMinimumWallThickness)),
                        Validators.max(Number(this.portalMaximumWallThickness)),
                        gapValidator(
                            this.portalMinimumWallThickness,
                            this.portalMaximumWallThickness,
                            this.portalGap
                        ),
                    ],
                    updateOn: 'change',
                },
            ],
            leftWidth: [
                portal
                    ? conf.doorPortal?.leftWidth
                    : this.selectedPortalOptions?.leftWidth
                    ? this.selectedPortalOptions.leftWidth
                    : this.leftSideWidths[0],
            ],
            rightWidth: [
                portal
                    ? conf.doorPortal?.rightWidth
                    : this.selectedPortalOptions?.rightWidth
                    ? this.selectedPortalOptions.rightWidth
                    : this.rightSideWidths[0],
            ],
            upperHeight: [
                portal
                    ? conf.doorPortal?.upperHeight
                    : this.selectedPortalOptions?.upperHeight
                    ? this.selectedPortalOptions.upperHeight
                    : this.upperHeights[0],
            ],
        });
    }

    setPortalSizesForPortalSides(
        leftSideWidths: number[],
        rightSideWidths: number[],
        upperHeights: number[]
    ) {
        this.leftSideWidths = leftSideWidths.sort((a, b) => a - b);
        this.rightSideWidths = rightSideWidths.sort((a, b) => a - b);
        this.upperHeights = upperHeights.sort((a, b) => a - b);
    }

    changeCasingBasedOnInnerWidthAndWallOverlap() {
        this.selectedCasing = this.casingsService.getCasingBasedOnInnerWidthAndWallOverlap(
            this.casingForm.get('casingWallWidth')?.value - this.casingHitchPoint,
            this.casingForm.get('casingWallOverlap')?.value,
            this.casings
        );
    }

    ngOnDestroy() {
        this.formSubscriptions.unsubscribe();
    }

    setInitialCasingSetup(casings: any, conf = this.configurationsService.conf.Current) {
        this.isCasingAvailableForFrame = this.casingsService.isCasingAvailableForFrame;
        this.casingHitchPoint = this.pageData.profile.casingHitchPoint;
        this.isCasingOptionsSelected = WindowActiveConfiguration.is(conf) && conf.Casing?.isCasingSelected != null;
        this.lowestInnerCasingWidth =
            Math.min(...casings.map((c) => c.innerCasingWidth)) + this.casingHitchPoint;
        this.highestInnerCasingWidth =
            Math.max(...casings.map((c) => c.innerCasingWidth)) + this.casingHitchPoint;
    }

    createCasingFormGroup(
        casings: any,
        casingHitchPoint: number,
        conf = this.configurationsService.conf.Current
    ) {
        let casingWallWidth;
        let casingWallOverlap;
        const isSelectedCasingAvailable = casings.find((c) => c.id === conf.Casing.id);

        if (isSelectedCasingAvailable) {
            this.casingWallOverlap = this.casingsService.getWallOverlapsBasedOnInnerCasingWidth(
                this.casings,
                isSelectedCasingAvailable.innerCasingWidth
            );
            casingWallWidth = Number(conf.Casing?.innerWidth) + casingHitchPoint;
            casingWallOverlap = conf.Casing?.wallOverlap;
        }

        this.casingForm = this.fb.group({
            casingWallWidth: [
                casingWallWidth ? casingWallWidth : this.casingWallWidth,
                {
                    validators: [
                        Validators.min(Number(this.lowestInnerCasingWidth)),
                        Validators.max(Number(this.highestInnerCasingWidth)),
                        gapValidator(
                            this.lowestInnerCasingWidth,
                            this.highestInnerCasingWidth,
                            this.CASING_GAP
                        ),
                    ],
                    updateOn: 'change',
                },
            ],
            casingWallOverlap: [
                casingWallOverlap ? casingWallOverlap : casings[0].casingWallOverlap,
            ],
        });
    }

    toggleCasing(event: MatCheckbox) {
        if (event.checked) {
            this.isCasingOptionsSelected = true;
            this.casingsService.setIsCasingAvailableForFrame(true);
        } else {
            this.isCasingOptionsSelected = false;
            this.casingsService.setIsCasingAvailableForFrame(false);
        }
    }

    toggleStandardLength(event: MatCheckbox) {
        if (this.profile) {
            this.profile.isDefaultLength = event.checked;
            if (!event.checked) {
                this.form.get('length')?.setValue(0);
            } else {
                this.validateLength();
            }
        }
    }

    add(conf = this.configurationsService.conf.Current) {
        if (!this.validateCouplingRangeOnAdd()) {
            return;
        }

        if (this.profile?.notStandardLength && this.profile.isDefaultLength === false && this.invalidLength) {
            return;
        }
        if (
            this.currentConfiguratorService.conf !== 'complementary_goods' &&
            this.currentConfiguratorService.conf !== 'coupled_window'
        ) {
            this.casings.length > 0 && this.addCasing(conf);
            this.availablePortals.length > 0 && this.addPortal(conf);
        }

        if (this.profile && this.form?.value) {
            Object.assign(this.profile, this.form.value);
            if (this.profile.widthOutType && this.profile.selectedWidth !== undefined && this.profile.selectedWidth !== null) {
                this.profile.widthOut = this.profile.selectedWidth;
            }

            // Czyścimy ostrzeżenia zakresów po poprawnym dodaniu profilu.
            this.issuesService.unregister('profile-width-out-of-range', conf);
            this.issuesService.unregister('profile-angle-out-of-range', conf);

            this.sharedFacade.closePage(this.profile);
        }
    }

    private validateCouplingRangeOnAdd(): boolean {
        if (!this.profile || !this.profile.widthOutType) {
            return true;
        }

        const current = this.configurationsService.conf.Current;
        const width = Number(this.profile.selectedWidth ?? this.profile.widthOut);
        const minWidth = Number(this.profile.widthOutMin);
        const maxWidth = Number(this.profile.widthOutMax);

        if (
            Number.isFinite(width)
            && Number.isFinite(minWidth)
            && Number.isFinite(maxWidth)
            && (width < minWidth || width > maxWidth)
        ) {
            const widthLabel = `${this.unitConverterService.milimetersToDimNoFormat(width)}${this.dimensionUnit.unitSymbol}`;
            const minWidthLabel = `${this.unitConverterService.milimetersToDimNoFormat(minWidth)}${this.dimensionUnit.unitSymbol}`;
            const maxWidthLabel = `${this.unitConverterService.milimetersToDimNoFormat(maxWidth)}${this.dimensionUnit.unitSymbol}`;
            const widthIssueTitle = this.translateService.instant('WINDOW|Profil poza zakresem szerokości');
            const widthIssueContent = this.translateService.instant(
                'WINDOW|Profil o szerokości {width} nie może zostać zastosowany. Dozwolony zakres szerokości: {minWidth} - {maxWidth}.',
                {
                    width: widthLabel,
                    minWidth: minWidthLabel,
                    maxWidth: maxWidthLabel,
                }
            );

            this.issuesService.simpleRegister(
                'profile-width-out-of-range',
                widthIssueTitle,
                widthIssueContent,
                current,
                {
                    logLevel: IssueLevel.WARN,
                }
            );
            return false;
        }

        if (this.profile.couplingType === 'angle_coupling') {
            const angle = Number(this.profile.angle);
            const minAngle = Number(this.profile.minAngle);
            const maxAngle = Number(this.profile.maxAngle);
            const angleLabel = Number.isFinite(angle) ? angle : 0;

            if (
                (Number.isFinite(angle)
                && Number.isFinite(minAngle)
                && Number.isFinite(maxAngle)
                && (angle < minAngle || angle > maxAngle)) || (!this.profile.angle || this.profile.angle === 0 || this.profile.angle === null || this.profile.angle === undefined)
            ) {
                const angleIssueTitle = this.translateService.instant('WINDOW|Profil poza zakresem kąta');
                const angleIssueContent = this.translateService.instant(
                    'WINDOW|Profil z kątem {angle}° nie może zostać zastosowany. Dozwolony zakres kąta: {minAngle}° - {maxAngle}°.',
                    {
                        angle: angleLabel,
                        minAngle,
                        maxAngle,
                    }
                );

                this.issuesService.simpleRegister(
                    'profile-angle-out-of-range',
                    angleIssueTitle,
                    angleIssueContent,
                    current,
                    {
                        logLevel: IssueLevel.WARN,
                    }
                );
                return false;
            }
        }

        return true;
    }

    private addCasing(conf = this.configurationsService.conf.Current) {
        const selectedCasing = this.selectedCasing || conf.Casing;
        this.isCasingAvailableForFrame = this.casingsService.isCasingAvailableForFrame;

        if (this.isCasingAvailableForFrame !== true) {
            this.casingsService.unsetCasing();
        } else {
            this.casingsService.setCasing(
                selectedCasing,
                this.casingForm.get('casingWallWidth')?.value - this.casingHitchPoint,
                this.casingForm.get('casingWallOverlap')?.value,
                this.casingHitchPoint
            );
        }
    }

    isPortalAvailable(conf = this.configurationsService.conf.Current) {
        const portalId =
            (this.availablePortals[0]?.id && conf.doorPortal?.id) ?? this.availablePortals[0]?.id;
        return (
            portalId &&
            this.pageData.profile?.type === 'frame' &&
            this.doorPortalsService.isPortalValid(portalId, conf)
        );
    }

    private addPortal(conf = this.configurationsService.conf.Current) {
        const isSelectedPortalAvailable = this.availablePortals.find(
            (p) => p.id === conf.doorPortal?.id
        );

        if (
            isSelectedPortalAvailable &&
            conf.doorPortal?.id &&
            this.selectedPortal?.id === conf.doorPortal?.id
        ) {
            this.selectedPortal = conf.doorPortal;
        }

        const portal = {
            selectedPortal: this.selectedPortal,
            plateType: this.portalForm.get('plateType')?.value,
            wallThickness: this.portalForm.get('wallThickness')?.value,
            depth:
                Number(this.portalForm.get('wallThickness')?.value) -
                this.additionalWallThicknessFromFrame,
            leftWidth: this.portalForm.get('leftWidth')?.value,
            rightWidth: this.portalForm.get('rightWidth')?.value,
            upperHeight: this.portalForm.get('upperHeight')?.value,
        };

        if ((portal && this.selectedPortal && this.isPortalSelected) || this.isDoorPortalFormOpen) {
            this.doorPortalsService.setPortal(portal);
        } else {
            this.doorPortalsService.unsetPortal();
        }
    }

    updateColorsSelect() {
        const conf = this.configurationsService.conf.Current;
        if (this.profile?.selectedColor) {
            const availableOuterColors = this.newColorsService.getMatchingColorsFor(
                conf,
                {
                    element: 'profile',
                    profile: this.profile,
                    side: 'outer',
                }
            );
            const availableInnerColors = this.newColorsService.getMatchingColorsFor(
                conf,
                {
                    element: 'profile',
                    profile: this.profile,
                    side: 'outer',
                }
            );
            const hasOuterWhiteColor = this.newColorsService.getWhiteElementColor(
                conf,
                {
                    element: 'profile',
                    profile: this.profile,
                    side: 'outer',
                }
            );
            const hasInnerWhiteColor = this.newColorsService.getWhiteElementColor(
                conf,
                {
                    element: 'profile',
                    profile: this.profile,
                    side: 'inner',
                }
            );
            let hasInnerWhiteColorWhenOuterIsWhite = null;
            if(hasOuterWhiteColor && this.profile.selectedColor?.outer) {
                const profileCopy = core.copy(this.profile);
                profileCopy.selectedColor = profileCopy.selectedColor ?? {
                    outer: null,
                    inner: null,
                    core: null,
                };
                const newOuterWhiteColor =  this.newColorsService.getMatchingConstrColor(
                    conf,
                    hasOuterWhiteColor,
                    profileCopy.selectedColor,
                    {
                        element: 'profile',
                        side: 'outer',
                        profile: profileCopy,
                    }
                )
                if(profileCopy.selectedColor && newOuterWhiteColor) {
                    profileCopy.selectedColor.outer = newOuterWhiteColor;
                }

                hasInnerWhiteColorWhenOuterIsWhite = this.newColorsService.getWhiteElementColor(
                    conf,
                    {
                        element: 'profile',
                        profile: profileCopy,
                        side: 'inner',
                    }
                );

            }


            const frameColor = WindowActiveConfiguration.is(conf) && conf.Colors.frame;

            this.hideExtensionColorSelection = WindowActiveConfiguration.is(conf) && conf.System?.hide_extension_color_selection && this.profile.type === 'extension';
            this.noFreeExtensionColorSelection = WindowActiveConfiguration.is(conf) && conf.System?.no_free_extension_color_selection && this.profile.type === 'extension';
            this.isWindowColor = frameColor &&
                this.profile.selectedColor.outer?.id === frameColor.outer?.id &&
                this.profile.selectedColor.inner?.id === frameColor.inner?.id &&
                this.profile.selectedColor.core?.id === frameColor.core?.id;

            this.whiteColorButtonVisible =
                !this.hideExtensionColorSelection &&
                this.isWindowColor &&
                (hasOuterWhiteColor && hasInnerWhiteColor || hasInnerWhiteColorWhenOuterIsWhite) &&
                (this.profile.selectedColor.outer?.type !== 'white' ||
                    this.profile.selectedColor.inner?.type !== 'white');

            const availableWindowColors = frameColor && availableOuterColors.some(
                (c) => c.id === frameColor.outer?.colorId
            ) && availableInnerColors.some((c) => c.id === frameColor.inner?.colorId);
            this.windowColorButtonVisible = frameColor && availableWindowColors && !this.hideExtensionColorSelection && !this.isWindowColor;
            this.anyColorButtonVisible =
                this.canChangeProfileColor
                && (this.showColorsWhenCoupling
                    || this.showColorsInComplementary
                    || (!this.hideExtensionColorSelection
                        && !this.windowColorButtonVisible
                        && !this.noFreeExtensionColorSelection
                        && !this.whiteColorButtonVisible
                        && (availableOuterColors.length > 1 || availableInnerColors.length > 1)));
        } else {
            this.whiteColorButtonVisible = false;
            this.windowColorButtonVisible = false;
            this.anyColorButtonVisible = false;
        }
    }

    setWhiteColors() {
        const conf = this.configurationsService.conf.Current;
        if (this.profile) {
            this.newColorsService.setWhiteColor(conf, {
                element: 'profile',
                profile: this.profile,
                side: 'outer',
            });
            this.newColorsService.setWhiteColor(conf, {
                element: 'profile',
                profile: this.profile,
                side: 'inner',
            });
            if (this.profile.selectedColor) {
                const colorFactor = this.getProfileColorFactor(
                    this.profile,
                    this.profile.selectedColor
                );

                this.profile.price = this.profilesPriceService.getProfilePrice(
                    this.profile.id,
                    this.profile.type,
                    null,
                    this.profile.selectedColor,
                    this.pageData.profilesPrices,
                    undefined,
                    undefined,
                    undefined,
                    colorFactor,
                );
                this.profile.price = this.normalizePartialPrice(this.profile.price);
            }

            this.updateColorsSelect();
        }
    }

    setWindowColors() {
        const conf = this.configurationsService.conf.Current;
        if (this.profile) {
            this.newColorsService.setDefaultColorFor(conf, {
                element: 'profile',
                profile: this.profile,
                side: 'outer',
            });
            this.newColorsService.setDefaultColorFor(conf, {
                element: 'profile',
                profile: this.profile,
                side: 'inner',
            });

            if((!this.profile.selectedColor?.outer?.coreColorId && !this.profile.selectedColor?.inner?.coreColorId) && this.profile.selectedColor?.core) {
                this.profile.selectedColor.core = null;
            }

            if (this.profile.selectedColor) {
                const colorFactor = this.getProfileColorFactor(
                    this.profile,
                    this.profile.selectedColor
                );

                this.profile.price = this.profilesPriceService.getProfilePrice(
                    this.profile.id,
                    this.profile.type,
                    null,
                    this.profile.selectedColor,
                    this.pageData.profilesPrices,
                    undefined,
                    undefined,
                    undefined,
                    colorFactor,
                );
                this.profile.price = this.normalizePartialPrice(this.profile.price);
            }
            this.updateColorsSelect();
        }
    }

    openModalColors() {
        if (this.profile) {
            this.profileColorsService
                .openProfileColorsModal(
                    core.copy(this.profile),
                    core.copy(this.profile),
                    this.pageData.profilesPrices,
                    this.pageData.selectedColors$,
                    true
                )
                .then((selection) => {
                    if (this.profile) {
                        const profileColors = selection
                            ? selection.colors
                            : this.profile.selectedColor;
                        const colorFactor = this.getProfileColorFactor(
                            this.profile,
                            profileColors
                        );

                        this.profile.price = this.profilesPriceService.getProfilePrice(
                            this.profile.id,
                            this.profile.type,
                            null,
                            profileColors,
                            this.pageData.profilesPrices,
                            undefined,
                            undefined,
                            undefined,
                            colorFactor,
                        );
                        this.profile.price = this.normalizePartialPrice(this.profile.price);

                        this.profile.selectedColor = core.copy(
                            selection ? selection.colors : this.profile.selectedColor
                        );
                        this.profile.selectedWood = core.copy(
                            selection ? selection.wood : this.profile.selectedWood
                        );
                        this.updateColorsSelect();
                        this.pageData.profile$.next(this.form.value);
                    }
                });
        }
    }

    openModalReinforcements(profile: Profile) {
        this.reinforcementsModalService
            .openReinforcementsModal(
                profile.id,
                profile.reinforcement ? profile.reinforcement : null,
                this.currency,
            )
            .then((reinforcement: Reinforcement | false) => {
                if (reinforcement) {
                    if (this.profile) {
                        this.profile.reinforcement =
                            reinforcement.id === null ? null : core.copy(reinforcement);
                    }
                }
            });
    }

    getPrice() {
        let price = null;
        if (this.profile && this.profile.selectedColor) {
            const colorFactor = this.getProfileColorFactor(
                this.profile,
                this.profile.selectedColor
            );

            price = this.profilesPriceService.getProfilePrice(
                this.profile.id,
                this.profile.type,
                null,
                this.profile.selectedColor,
                this.pageData.profilesPrices,
                undefined,
                undefined,
                undefined,
                colorFactor,
            );
            price = this.normalizePartialPrice(price);
            this.updateColorsSelect();
        }

        return !isNaN(price) && price !== null
            ? price
            : this.translateService.instant('CONFIGURATOR|Do wyceny');
    }


    hasZeroPrice(
        price: {
            price_field: any;
            price_length: any;
            price_construction: any;
            price_piece: any;
            price_sash: any;
            price_area: any;
            price_percent: any;
        },
        type: ProfileType | null = null
    ) {
        if (!price) {
            return false;
        }
        let requiredFields = [price.price_field, price.price_length, price.price_piece];
        if (type === 'frame') {
            requiredFields.push(price.price_percent);
            requiredFields.push(price.price_construction);
        }
        if (type === 'threshold') {
            requiredFields = [price.price_construction, price.price_sash, price.price_length];
        } else if (
            ['coupling', 'extension', 'alignment', 'other', 'fixed_mullion', 'structured_muntin_sash', 'structured_muntin_frame'].includes(type || '')
        ) {
            requiredFields = [price.price_piece, price.price_length];
        } else if (type === 'sandwich') {
            requiredFields = [price.price_area];
        }
        return requiredFields.every((p) => p === 0);
    }

    hasNoPrice(
        price: {
            price_field: any;
            price_length: any;
            price_piece: any;
            price_construction: any;
            price_sash: any;
            price_area: any;
            price_percent: any;
        },
        type: ProfileType | null = null
    ) {
        if (!price) {
            return true;
        }
        let requiredFields = [price.price_field, price.price_length, price.price_piece];
        if (type === 'frame') {
            requiredFields.push(price.price_percent);
            requiredFields.push(price.price_construction);
        }
        if (type === 'threshold') {
            requiredFields = [price.price_construction, price.price_sash, price.price_length];
        } else if (
            ['coupling', 'extension', 'alignment', 'other', 'fixed_mullion', 'structured_muntin_sash', 'structured_muntin_frame'].includes(type || '')
        ) {
            requiredFields = [price.price_piece, price.price_length];
        } else if (type === 'sandwich') {
            requiredFields = [price.price_area];
        }
        return requiredFields.some((p) => p === null || isNaN(Number(p)));
    }

    priceMarket(price: number) {
        return this.priceBaseService.addMarginMarketFactor(price);
    }

    clickShowInfoButton() {
        this.modalService.open({
            pageComponent: ProfileInfoComponent,
            resolve: {
                profile: () => this.profile,
                button: () => false,
            },
        });
    }

    isDoor() {
        return this.currentConfiguratorService.conf === 'door';
    }

    validateLength() {
        const length = this.form.get('length')?.value;

        if (length >= this.profile?.minLength && length <= this.profile?.maxLength && length % this.profile?.lengthStep === 0) {
            this.invalidLength = false;
        } else {
            this.invalidLength = true;
        }
    }
    hasReinforcementsForProfile(profile: Profile): boolean {
        return this.reinforcementsService.hasReinforcementsForProfile(profile);
    }
}
